import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { paymentMethodLabel } from '@/constants/service-payment-methods';
import { RECEIVABLES_RELEASING } from '@/content/payments/receivables';
import type { PaymentReleasingCharge } from '@/types/payment-reporting';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';

/**
 * Um pagamento que ainda não liberou: cliente, serviço, forma e dia do
 * pagamento à esquerda; valor líquido à direita. `showReleaseDate` põe
 * "Libera em …" embaixo do valor — na lista "Liberando", onde cada linha tem
 * a própria data; dentro de um dia da agenda a data já está no cabeçalho.
 * O dia vem de `availableDate` (já no fuso do prestador, calculado no
 * backend), nunca de `new Date(availableOn)` no aparelho.
 */
export function ReleasingChargeRow({
  charge,
  showReleaseDate = false,
  isLast = false,
}: {
  charge: PaymentReleasingCharge;
  showReleaseDate?: boolean;
  isLast?: boolean;
}) {
  const details = [
    charge.serviceName,
    charge.paymentMethodUsed ? paymentMethodLabel(charge.paymentMethodUsed) : null,
    charge.paidAt ? `${RECEIVABLES_RELEASING.paidOn} ${formatDate(charge.paidAt, 'dd/MM')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View
      className={cn(
        'flex-row items-start justify-between gap-3 py-3',
        !isLast && 'border-b border-neutral-100',
      )}
      testID={`releasing-charge-${charge.id}`}
    >
      <View className="min-w-0 flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm text-neutral-900" numberOfLines={1}>
          {charge.clientName || RECEIVABLES_RELEASING.clientFallback}
        </ThemedText>
        {details ? (
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {details}
          </ThemedText>
        ) : null}
      </View>
      <View className="shrink-0 items-end gap-0.5">
        <ThemedText
          className="font-bold text-sm text-neutral-900"
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {formatCurrency(charge.amount)}
        </ThemedText>
        {showReleaseDate ? (
          <ThemedText className="text-xs text-neutral-500">
            {RECEIVABLES_RELEASING.releasesOn} {formatDate(charge.availableDate, "EEE, dd/MM")}
          </ThemedText>
        ) : null}
      </View>
    </View>
  );
}
